import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { ROTULO_SITUACAO_PAINEL_FICHA, SITUACOES_PAINEL_FICHA, type PainelFichaLinha, type SituacaoPainelFicha } from '@triade/contracts';
import { useCategorias } from '@/api/categorias';
import { usePainelFichas } from '@/api/fichas';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista } from '@/components/barra-lista';
import { SeletorCategoria } from '@/components/categorias/seletor-categoria';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { useDebounced } from '@/hooks/use-debounced';
import { formatarCusto, formatarDataHora } from '@/lib/format';
import { BotaoRelatorios } from '@/relatorios';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import {
  motivoCustoIndisponivel, paraConsultaPainel, ROTULO_TIPO_PAINEL_FICHA, TAMANHO_PAGINA_FICHAS, temFiltroFicha, TIPOS_PAINEL_FICHA, type BuscaFicha,
} from './tipos';

const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';
const ESTILO_SITUACAO: Record<SituacaoPainelFicha, string> = {
  ESCOLHIDA: 'bg-success-bg text-success-ink',
  SEM_ESCOLHIDA: 'bg-warning-bg text-warning-ink',
  SEM_FICHA: 'bg-line-soft text-ink-body',
};

/**
 * Lista de fichas tecnicas (`/custos/fichas-tecnicas` sem `itemId`): uma linha por item PRODUTO/SERVICO com a situacao da ficha.
 * Busca, filtros e pagina vem da URL (`BuscaFicha`); a API filtra e pagina (GET /fichas-tecnicas/painel).
 */
export function FichasListaPage({ busca }: { busca: BuscaFicha }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const podeCriar = usePode().pode('ficha.editar');
  const pagina = busca.pagina ?? 1;
  const painel = usePainelFichas(paraConsultaPainel(busca));
  const categorias = useCategorias();

  const mudar = useCallback(
    (mudanca: Partial<BuscaFicha>) => {
      void navigate({ to: '/custos/fichas-tecnicas', search: { ...busca, ...mudanca }, replace: true });
    },
    [navigate, busca],
  );

  // Linha com ficha vai para a ficha; sem ficha, para a tela de entrada com o item (que oferece criar a primeira versao).
  const abrir = useCallback(
    (l: PainelFichaLinha) => {
      if (l.fichaId) void navigate({ to: '/custos/fichas-tecnicas/$id', params: { id: l.fichaId } });
      else void navigate({ to: '/custos/fichas-tecnicas', search: { itemId: l.itemId } });
    },
    [navigate],
  );

  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtraso = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtraso !== (busca.q ?? '')) mudar({ q: termoAtraso || undefined, pagina: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtraso]);
  useEffect(() => setTermo(busca.q ?? ''), [busca.q]);

  const total = painel.data?.total ?? 0;
  const linhas = painel.data?.itens ?? [];
  // Coluna de custo so existe quando a API devolveu `custo` (usuario com custo.ver).
  const temCusto = linhas.some((l) => l.custo !== undefined);
  const nomeCategoria = busca.categoriaId === 'SEM' ? 'Sem categoria' : categorias.data?.categorias.find((c) => c.id === busca.categoriaId)?.caminho;

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver as fichas técnicas.</Mensagem>;
  else if (painel.isError && !painel.data) corpo = <div className="p-4"><ErroFormacao erro={painel.error} onTentar={() => void painel.refetch()} /></div>;
  else if (!painel.data) corpo = <Mensagem papel="status">Carregando fichas técnicas…</Mensagem>;
  else if (linhas.length === 0) {
    corpo = temFiltroFicha(busca) ? (
      <Mensagem papel="status">
        Nenhuma ficha com esses filtros.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => void navigate({ to: '/custos/fichas-tecnicas', search: {}, replace: true })}>
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">Nenhum produto ou serviço ativo no cadastro.</Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Item</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Categoria</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Tipo</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Versão vigente</th>
              {temCusto && <th scope="col" className="px-3 py-2.5 font-medium">Custo</th>}
              <th scope="col" className="px-3 py-2.5 font-medium">Situação</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Atualizada em</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.itemId} data-testid="linha-ficha" onClick={() => abrir(l)} className="cursor-pointer border-t border-line-soft hover:bg-surface-muted">
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <LinkItem linha={l} />
                    {podeCriar && l.situacao === 'SEM_FICHA' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrir(l);
                        }}
                        className="h-7 rounded-lg border border-border bg-card px-2.5 text-caption font-medium text-primary hover:bg-surface-muted"
                      >
                        Criar ficha
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-ink-secondary">{l.categoriaCaminho ?? '—'}</td>
                <td className="px-3 py-3 text-ink-secondary">{ROTULO_TIPO_PAINEL_FICHA[l.tipo]}</td>
                <td className="tabular px-3 py-3 text-ink-secondary">{l.versao === null ? '—' : `v${l.versao}`}</td>
                {temCusto && (
                  <td className="tabular px-3 py-3">
                    <CustoCelula linha={l} />
                  </td>
                )}
                <td className="px-3 py-3">
                  <span data-situacao={l.situacao} className={cn('inline-flex rounded-full px-2.5 py-0.5 text-caption font-semibold', ESTILO_SITUACAO[l.situacao])}>
                    {ROTULO_SITUACAO_PAINEL_FICHA[l.situacao]}
                  </span>
                </td>
                <td className="tabular px-3 py-3 text-ink-secondary">{formatarDataHora(l.atualizadaEm)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5 pb-4">
      <section aria-label="Lista de fichas técnicas" aria-busy={painel.isFetching} className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex h-[34px] w-[240px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
              <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
              <label htmlFor="fichas-q" className="sr-only">
                Buscar item
              </label>
              <input
                id="fichas-q"
                type="search"
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                placeholder="Código ou descrição"
                autoComplete="off"
                className="w-full min-w-0 bg-transparent text-small outline-none"
              />
            </div>
            <label htmlFor="fichas-situacao" className="sr-only">
              Situação
            </label>
            <select
              id="fichas-situacao"
              value={busca.situacao ?? ''}
              onChange={(e) => mudar({ situacao: (e.target.value || undefined) as SituacaoPainelFicha | undefined, pagina: undefined })}
              className={CAMPO}
            >
              <option value="">Todas as situações</option>
              {SITUACOES_PAINEL_FICHA.map((s) => (
                <option key={s} value={s}>
                  {ROTULO_SITUACAO_PAINEL_FICHA[s]}
                </option>
              ))}
            </select>
            <label htmlFor="fichas-tipo" className="sr-only">
              Tipo
            </label>
            <select
              id="fichas-tipo"
              value={busca.tipo ?? ''}
              onChange={(e) => mudar({ tipo: (e.target.value || undefined) as BuscaFicha['tipo'], pagina: undefined })}
              className={CAMPO}
            >
              <option value="">Todos os tipos</option>
              {TIPOS_PAINEL_FICHA.map((t) => (
                <option key={t} value={t}>
                  {ROTULO_TIPO_PAINEL_FICHA[t]}
                </option>
              ))}
            </select>
            <SeletorCategoria
              id="fichas-categoria"
              rotulo="Categoria"
              ocultarRotulo
              comTodas
              value={busca.categoriaId}
              onChange={(v) => mudar({ categoriaId: v, pagina: undefined })}
              className="h-[34px] w-auto rounded-[9px]"
            />
          </div>
        </div>
        {corpo}
      </section>
      <BarraDeAcoesLista
        paginacao={{ pagina, tamanho: TAMANHO_PAGINA_FICHAS, total, onPagina: (p) => mudar({ pagina: p > 1 ? p : undefined }) }}
        relatorios={
          <BotaoRelatorios
            tela="fichas-lista"
            params={{ q: busca.q, situacao: busca.situacao, tipo: busca.tipo, categoriaId: busca.categoriaId, categoriaNome: nomeCategoria }}
          />
        }
      />
    </div>
  );
}

/** Item como link: com ficha, abre a ficha; sem ficha, abre a tela de entrada com o item. */
function LinkItem({ linha }: { linha: PainelFichaLinha }) {
  const classe = 'font-semibold text-ink no-underline hover:underline';
  const texto = `${linha.itemCodigo} · ${linha.itemDescricao}`;
  if (linha.fichaId) {
    return (
      <Link to="/custos/fichas-tecnicas/$id" params={{ id: linha.fichaId }} onClick={(e) => e.stopPropagation()} className={classe}>
        {texto}
      </Link>
    );
  }
  return (
    <Link to="/custos/fichas-tecnicas" search={{ itemId: linha.itemId }} onClick={(e) => e.stopPropagation()} className={classe}>
      {texto}
    </Link>
  );
}

/** Custo da ficha escolhida. Sem escolhida: "—". Escolhida sem custo: "indisponível", com o motivo no `title`. */
function CustoCelula({ linha }: { linha: PainelFichaLinha }) {
  if (linha.situacao !== 'ESCOLHIDA') return <>—</>;
  const custo = linha.custo ?? null;
  if (custo === null) {
    const motivo = motivoCustoIndisponivel(linha.estadoCusto);
    return (
      <span title={`Custo indisponível: ${motivo}`} className="text-warning-ink">
        indisponível
      </span>
    );
  }
  return <>{formatarCusto(custo)}</>;
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
